// @givmo/app-admin — Forgot-password dialog (ports `forgot_password_pop_up.dart`).
//
// Verbatim copy: "Forgot your password?" / "We will email you with instructions on how to reset it" /
// "Reset password" / "Back to login" / success toast "Reset link sent to <email>". Calls
// `sendPasswordReset` (→ @givmo/auth `adminSendPasswordReset`). Email is pre-filled from the sign-in
// form. Inline `Invalid email format` on a malformed email (no network call); a backend failure
// surfaces inline via the AuthError message.
//
// Drawn in the register of the sign-in screen that opens it (EP-WEB43): the house's dialog title,
// the SaaS button, a brand-coloured text control.

import { useState } from 'react';
import { Button, Dialog, TextField, ErrorMessage, toast, testIdProp } from '@givmo/design-system';
import { useAuth } from '../../auth/auth-context';
import { EMAIL_REGEX, INVALID_EMAIL_MESSAGE } from './auth-schemas';
import { useAuthError } from './use-auth-error';

export interface ForgotPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fill the email from the sign-in form. */
  initialEmail: string;
}

export function ForgotPasswordDialog({
  open,
  onOpenChange,
  initialEmail,
}: ForgotPasswordDialogProps) {
  const { sendPasswordReset } = useAuth();
  const [email, setEmail] = useState(initialEmail);
  const [formatError, setFormatError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { error, clearError, surface } = useAuthError();

  // Keep the field in sync when the dialog (re)opens with a different sign-in email.
  const [lastInitial, setLastInitial] = useState(initialEmail);
  if (open && initialEmail !== lastInitial) {
    setLastInitial(initialEmail);
    setEmail(initialEmail);
  }

  const onSubmit = async () => {
    clearError();
    setFormatError(false);
    if (!EMAIL_REGEX.test(email)) {
      setFormatError(true);
      return;
    }
    setSubmitting(true);
    try {
      await sendPasswordReset(email);
      toast.success(`Reset link sent to ${email}`);
      onOpenChange(false);
    } catch (thrown) {
      surface(thrown);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content aria-label="Forgot your password?" testId="auth-forgot-dialog">
        <div className="flex flex-col gap-16">
          <div className="flex flex-col gap-4">
            <h2 className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
              Forgot your password?
            </h2>
            <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
              We will email you with instructions on how to reset it
            </p>
          </div>
          <TextField
            label="Email address"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={formatError}
            errorMessage={INVALID_EMAIL_MESSAGE}
            testId="auth-forgot-email"
          />
          {error !== null && <ErrorMessage testId="auth-forgot-error">{error}</ErrorMessage>}
          <Button
            appearance="saas"
            tone="primary"
            size="lg"
            fullWidth
            loading={submitting}
            onClick={() => void onSubmit()}
            testId="auth-forgot-submit"
          >
            Reset password
          </Button>
          <Dialog.Close
            className="self-start font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
            {...testIdProp('auth-forgot-back')}
          >
            Back to login
          </Dialog.Close>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
